import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTrendingUp,FiZap,FiTarget,FiCheck,FiArrowRight,FiEye,FiUsers,FiDollarSign,FiClock,FiBarChart3,FiShield,FiStar,FiAlertCircle,FiLock,FiUnlock}=FiIcons;

function EngagementGrowthEngine() {
  const services=[
    {
      name: 'Engagement Foundation',
      description: 'For companies losing money on traffic that doesn\'t convert',
      timeline: '90-day implementation',
      features: [
        'Interactive lead magnets that capture attention',
        'Behavioral email sequences that nurture engagement',
        'Conversion optimization based on psychology',
        'Complete analytics and tracking setup',
        'Monthly optimization reviews'
      ],
      results: [
        '2-4x improvement in conversion rates',
        '50-80% increase in email engagement',
        'Detailed conversion audit and roadmap',
        '90-day money-back guarantee'
      ],
      idealFor: [
        'Companies with 5,000+ monthly visitors',
        'Businesses with conversion rates under 3%',
        'Teams ready to implement proven systems',
        'Organizations wanting measurable ROI'
      ]
    },
    {
      name: 'Engagement Accelerator',
      description: 'For companies ready to scale what works',
      timeline: '120-day transformation',
      features: [
        'Complete engagement ecosystem design',
        'Advanced automation and personalization',
        'Multi-channel engagement sequences',
        'Personalization at scale implementation',
        'Dedicated success manager',
        'Bi-weekly optimization sessions'
      ],
      results: [
        '4-8x improvement in conversion rates',
        '200-400% increase in qualified leads',
        'Automated engagement that works 24/7',
        'Complete customer journey optimization'
      ],
      idealFor: [
        'Companies with 10,000+ monthly visitors',
        'Businesses ready for advanced automation',
        'Teams with dedicated marketing resources',
        'Organizations scaling rapidly'
      ],
      popular: true
    },
    {
      name: 'Engagement Enterprise',
      description: 'For companies that need custom solutions',
      timeline: 'Custom timeline based on scope',
      features: [
        'Proprietary engagement platform development',
        'White-label engagement solutions',
        'Strategic consulting and planning',
        'Dedicated success team',
        'Custom integrations and development',
        'Ongoing strategic support'
      ],
      results: [
        '8-15x improvement in conversion rates',
        'Custom engagement platform ownership',
        'Competitive advantage through innovation',
        'Enterprise-level support and consulting'
      ],
      idealFor: [
        'Companies with 25,000+ monthly visitors',
        'Enterprises needing custom solutions',
        'Organizations with complex requirements',
        'Businesses wanting proprietary advantages'
      ]
    }
  ];

  const faqs=[
    {
      question: 'How do I know this will work for my business?',
      answer: 'I don\'t work with companies unless I can show them exactly how we\'ll improve their numbers. Every engagement starts with a conversion audit that identifies your specific leaks. You\'ll see the potential before we begin any work.'
    },
    {
      question: 'What if we\'re in a niche industry?',
      answer: 'I\'ve worked across 23 industries. The psychology of engagement is universal—people need to feel invested before they convert,regardless of what you sell. The tactics may vary,but the principles remain consistent.'
    },
    {
      question: 'We\'ve tried conversion optimization before.',
      answer: 'Most agencies optimize the wrong things. They focus on button colors and headlines. We focus on engagement psychology—understanding why people leave and creating systems that keep them invested. The difference is measurable.'
    },
    {
      question: 'How quickly will we see results?',
      answer: 'Most clients see improvement within 30 days. Full implementation takes 90-120 days. But the changes compound—month 6 results are typically 3x better than month 1. We track everything so you can see progress in real-time.'
    },
    {
      question: 'What makes this different from other conversion services?',
      answer: 'We don\'t just optimize—we transform how visitors engage with your brand. Instead of hoping people convert,we create systems that make them want to stay,explore,and ultimately buy. It\'s psychology-driven,not just data-driven.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-900 via-red-800 to-orange-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            98% of Your Visitors Leave Without Converting. <br />
            <span className="text-red-200">Here's Why.</span>
          </h1>
          <p className="text-xl md:text-2xl text-red-100 max-w-4xl mx-auto mb-10">
            After analyzing 500+ websites,I discovered the real reason most businesses struggle with conversion. It's not traffic. It's engagement. And it's costing you millions.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">500+</div>
              <div className="text-red-200">Websites Analyzed</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">247%</div>
              <div className="text-red-200">Average Conversion Improvement</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">89%</div>
              <div className="text-red-200">Miss These 3 Engagement Leaks</div>
            </div>
          </div>
          <Link
            to="/book-consultation"
            className="bg-white text-red-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-red-50 transition-colors inline-flex items-center space-x-2"
          >
            <SafeIcon icon={FiEye} className="h-5 w-5" />
            <span>Show Me The 3 Leaks</span>
          </Link>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The $4.4M Problem Hiding in Plain Sight
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 mb-8">
                Every month,your website gets thousands of visitors. Most leave within 8 seconds.
              </p>
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                    <SafeIcon icon={FiAlertCircle} className="h-6 w-6 mr-2" />
                    Your Current Reality
                  </h3>
                  <div className="space-y-2 text-gray-700">
                    <div>10,000 monthly visitors × <span className="font-semibold text-red-600">2% conversion</span>=200 leads</div>
                    <div>200 leads × 15% close rate=30 customers</div>
                    <div>30 customers × $5,000 value=<span className="font-bold text-red-600">$150,000 revenue</span></div>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                    <SafeIcon icon={FiUnlock} className="h-6 w-6 mr-2" />
                    With Proper Engagement
                  </h3>
                  <div className="space-y-2 text-gray-700">
                    <div>10,000 visitors × <span className="font-semibold text-green-600">6.9% conversion</span>=690 leads</div>
                    <div>690 leads × 15% close rate=104 customers</div>
                    <div>104 customers × $5,000 value=<span className="font-bold text-green-600">$520,000 revenue</span></div>
                  </div>
                </div>
              </div>
              <div className="text-center bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="text-2xl font-bold text-yellow-800 mb-2">
                  The Difference: $4.4M Annually
                </div>
                <p className="text-gray-700">
                  Most agencies tell you to buy more traffic. We help you convert the traffic you already have.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The Engagement-First Method
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              After studying 847 high-converting companies,I found they all do 4 things differently:
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      They Lead With Curiosity,Not Features
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Instead of "Start Free Trial," they use "See Your Potential ROI"
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <div className="text-red-600 mb-1">❌ "Start Your Free Trial"</div>
                      <div className="text-green-600">✅ "Calculate Your ROI in 60 Seconds"</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      They Provide Value Before Asking for Value
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Interactive tools,assessments,calculators—not just contact forms
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <div className="text-red-600 mb-1">❌ "Fill out this form to learn more"</div>
                      <div className="text-green-600">✅ "Get your custom strategy in 2 minutes"</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      They Personalize Based on Behavior,Not Demographics
                    </h3>
                    <p className="text-gray-600 mb-4">
                      What someone does matters more than who they are
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <div className="text-red-600 mb-1">❌ "Hi [First Name]"</div>
                      <div className="text-green-600">✅ "Since you viewed our pricing page..."</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      They Automate Engagement,Not Just Email
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Every interaction is designed to increase investment
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <div className="text-red-600 mb-1">❌ "Thanks for subscribing!"</div>
                      <div className="text-green-600">✅ "Here's your personalized action plan..."</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Three Ways to Fix Your Conversion Problem
            </h2>
          </div>
          <div className="space-y-8">
            {services.map((service,index)=> (
              <div key={index} className={`bg-white rounded-2xl p-8 lg:p-12 shadow-lg ${service.popular ? 'ring-2 ring-blue-500' : ''} relative`}>
                {service.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-1">
                      <SafeIcon icon={FiStar} className="h-4 w-4" />
                      <span>Most Popular</span>
                    </span>
                  </div>
                )}
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="mb-6">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-2xl font-bold text-gray-900">{service.name}</h3>
                      </div>
                      <p className="text-lg text-gray-600 mb-2">{service.description}</p>
                      <p className="text-sm text-gray-500">{service.timeline}</p>
                    </div>
                    <div className="mb-8">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">What's Included:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature,idx)=> (
                          <li key={idx} className="flex items-start space-x-3">
                            <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mb-8">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Ideal For:</h4>
                      <ul className="space-y-2">
                        {service.idealFor.map((item,idx)=> (
                          <li key={idx} className="flex items-start space-x-3">
                            <SafeIcon icon={FiArrowRight} className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Expected Results:</h4>
                    <ul className="space-y-3 mb-6">
                      {service.results.map((result,idx)=> (
                        <li key={idx} className="flex items-start space-x-3">
                          <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{result}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/book-consultation"
                      className="block w-full text-center bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                    >
                      Get Your Conversion Audit
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-8">
            {faqs.map((faq,index)=> (
              <div key={index} className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-red-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Stop Losing Money on Traffic That Doesn't Convert
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-4xl mx-auto">
            Every day you wait is money left on the table. Get your conversion audit and discover exactly how much revenue you're missing.
          </p>
          <div className="bg-white/10 rounded-xl p-6 max-w-2xl mx-auto mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Your Conversion Audit Includes:</h3>
            <ul className="text-left space-y-2">
              {[
                'Complete analysis of your current conversion funnel',
                'Identification of the 3 biggest engagement leaks',
                'Custom strategy to fix your conversion problems',
                'ROI projections based on your actual traffic',
                'Implementation roadmap with timeline and priorities'
              ].map((item,idx)=> (
                <li key={idx} className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-300 mt-0.5 flex-shrink-0" />
                  <span className="text-red-100">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <Link
            to="/book-consultation"
            className="bg-white text-red-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-red-50 transition-colors inline-flex items-center space-x-2"
          >
            <SafeIcon icon={FiBarChart3} className="h-5 w-5" />
            <span>Get My Conversion Audit</span>
          </Link>
          <div className="mt-6 text-red-200 text-sm">
            ⚡ Limited spots available - We only take on 5 new audits per month
          </div>
        </div>
      </section>
    </div>
  );
}

export default EngagementGrowthEngine;